import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {useDragScrollable} from '@containers/drag-scrollable'
import {useBoard, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Shift, TaskType, useCalendarStore} from '../calendar-store'
import {
  getTasksWithLargeShift,
  getTasksWithShiftLeft,
  getTasksWithShiftRight,
  hours
} from '../utils'
import {AllDayTasks} from './all-day/all-day-tasks'
import {Day} from './day'
import {useDraggableStore} from './draggable/draggable-store'
import {Header} from './header/header'
import {HeaderDays} from './header/header-days'
import {MoveOverlay} from './move-overlay'
import {Nav} from './nav/nav'

export const allDayTasksContainerId = 'all-day-tasks-container-id'
export const regularTasksContainerId = 'regular-tasks-container-id'

const AllDayTasksTw = tw.div`
  sticky
  z-30
  min-w-full
  overflow-y-hidden
  top-40
  w-max
`

const CellTw = tw.div`
  flex
`

export const PlaceholderTw = tw.div`
  h-30
  pl-8
  pr-2
  border
  border-solid
  rounded-4
  border-bg-main-old
  shadow-task-old
  bg-obscure-constant-old
  absolute
  w-full
  z-20
  opacity-60
  pointer-events-none
`

interface Props {
  shift: Shift
}

// eslint-disable-next-line max-lines-per-function
export const Week = observer(({shift}: Props) => {
  const [refScrollable, mouseDown] = useDragScrollable({
    direction: 'scroll-xy',
    shouldActivate: true
  })
  const board = useBoard()
  const [state, store] = useCalendarStore()
  const [draggableState] = useDraggableStore()

  useSyncEvent(
    'current',
    {debounceTime: 500, filter: '*', onlyRemote: true},
    () => {
      store.calculateCurrentTasks(board, shift)
    },
    [board, state.weekNumber.current]
  )

  useEffect(() => {
    refScrollable.current.scrollTop = 400
    store.calculateCurrentTasks(board, shift)
  }, [board, state.weekNumber.current])

  const tasksWithStartDate = store
    .getTasksWithStartDate(shift)
    .sort((a, b) =>
      a.task.startDate < b.task.startDate
        ? -1
        : a.task.startDate > b.task.startDate
        ? 1
        : 0
    )

  const updateScrollPosition = () => {
    if (refScrollable.current) {
      const scrollLeft = refScrollable.current.scrollLeft
      const offsetLeft = refScrollable.current.offsetLeft
      const scrollWidth = refScrollable.current.scrollWidth

      if (offsetLeft > scrollWidth) {
        store.updateScrollPosition('none')
      } else if (scrollWidth - scrollLeft < offsetLeft) {
        state.scrollPosition !== 'right-corner' &&
          store.updateScrollPosition('right-corner')
      } else if (scrollLeft <= 10) {
        state.scrollPosition !== 'left-corner' &&
          store.updateScrollPosition('left-corner')
      } else {
        state.scrollPosition !== 'middle-area' &&
          store.updateScrollPosition('middle-area')
      }
    }
  }

  const mouseUp = () => {
    store.setCursorGrabbing(false)
    document.removeEventListener('mouseup', mouseUp)
    document.removeEventListener('mousemove', mouseMove)
  }

  const mouseMove = () => {
    updateScrollPosition()
  }

  const mouseDownEvent = (e: React.MouseEvent) => {
    mouseDown(e)
    document.addEventListener('mouseup', mouseUp)
    document.addEventListener('mousemove', mouseMove)
  }
  const tasksWithShiftLeft = getTasksWithShiftLeft(tasksWithStartDate)
  const tasksWithShifts = getTasksWithShiftRight(tasksWithShiftLeft)
  const tasks = getTasksWithLargeShift(tasksWithShifts)
  const types: TaskType[] = [
    'all-day-multiple',
    'all-day-single',
    'all-day-with-time',
    'from-day-to-hour'
  ]

  const dragCondition = types.some(type =>
    draggableState.draggedItem.taskType?.includes(type)
  )

  return (
    <div className="w-full h-full">
      <div className="flex flex-col w-full h-full">
        <Header shift={shift} />
        <div
          className="overflow-auto bg-main-old"
          style={{height: 'calc(100% - 168px)'}}
          ref={refScrollable}
          onMouseDown={e => {
            if (!state.openedPopup) {
              store.setCursorGrabbing(true)
              mouseDownEvent(e)
            }
          }}
        >
          <div className="flex">
            <Nav />
            <div style={{width: 'calc(100% - 66px)'}}>
              <div
                id={allDayTasksContainerId}
                className={cn(
                  'sticky top-0 z-30',
                  draggableState.draggedItem.taskType === 'from-hour-to-day' &&
                    'cursor-grabbing'
                )}
              >
                <HeaderDays shift={shift} />
                <AllDayTasksTw>
                  <AllDayTasks shift={shift} />
                </AllDayTasksTw>
              </div>
              <div
                id={regularTasksContainerId}
                className={cn(
                  'my-16 sticky min-w-full w-max',
                  draggableState.draggedItem.taskType === 'from-day-to-hour' &&
                    'cursor-grabbing'
                )}
              >
                {draggableState.draggedItem.id && (
                  <MoveOverlay allDay={dragCondition} />
                )}
                {hours.map(hour => (
                  <CellTw key={hour}>
                    <Day
                      currentHour={hour}
                      shift={shift}
                      tasksWithStartDate={tasks}
                    />
                  </CellTw>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})
